import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme definitions (Order: Entspannt, Fokus, Mindset)
// IMPORTANT: Ensure good contrast ratios (WCAG AA minimum 4.5:1 for text)
export const themes = {
  relaxation: {
    name: 'Entspannt',
    nameEn: 'Relaxed',
    description: 'Ruhig und harmonisch',
    descriptionEn: 'Calm and harmonious',
    icon: '☀️',
    colors: {
      light: {
        primary: '#b8865f', // deeper golden for better contrast
        primaryGlow: 'rgba(184, 134, 95, 0.15)',
        background: '#fefdfb', // slightly off-white
        foreground: '#1f1815', // very dark brown (high contrast)
        card: '#ffffff',
        cardForeground: '#1f1815', // very dark brown
        muted: '#f5f0e8', // light beige
        mutedForeground: '#4a4037', // darker brown for readability
        accent: '#c9925f', // stronger golden
        accentForeground: '#ffffff', // WHITE text on golden for CTA sections
      },
      dark: {
        primary: '#e8b882', // bright golden for visibility
        primaryGlow: 'rgba(232, 184, 130, 0.2)',
        background: '#0f0d0b', // very dark for contrast
        foreground: '#f5f0e8', // light beige text
        card: '#1f1815', // dark brown card
        cardForeground: '#f5f0e8', // light text on dark
        muted: '#2d2520', // muted dark
        mutedForeground: '#d4cec4', // light for readability
        accent: '#f0c896', // bright accent
        accentForeground: '#0f0d0b', // very dark text
      }
    }
  },
  focus: {
    name: 'Fokus',
    nameEn: 'Focus',
    description: 'Klar und konzentriert',
    descriptionEn: 'Clear and focused',
    icon: '🎯',
    colors: {
      light: {
        primary: '#2b7de9', // darker blue for contrast
        primaryGlow: 'rgba(43, 125, 233, 0.15)',
        background: '#fafcff', // slightly off-white
        foreground: '#0c1e2f', // very dark navy (high contrast)
        card: '#ffffff',
        cardForeground: '#0c1e2f', // very dark navy
        muted: '#e6f2ff', // light blue
        mutedForeground: '#1e3a52', // dark blue for readability
        accent: '#3d8bef', // strong blue
        accentForeground: '#0c1e2f', // dark text on blue
      },
      dark: {
        primary: '#64b5f6', // bright blue for visibility
        primaryGlow: 'rgba(100, 181, 246, 0.2)',
        background: '#050c14', // very dark for contrast
        foreground: '#e6f2ff', // light blue text
        card: '#0c1e2f', // dark navy card
        cardForeground: '#e6f2ff', // light text on dark
        muted: '#1a2e42', // muted navy
        mutedForeground: '#b8d4ea', // light for readability
        accent: '#82c4f8', // bright accent
        accentForeground: '#050c14', // very dark text
      }
    }
  },
  mindset: {
    name: 'Mindset',
    nameEn: 'Mindset',
    description: 'Ausgeglichen und harmonisch',
    descriptionEn: 'Balanced and harmonious',
    icon: '🧘',
    colors: {
      light: {
        primary: '#7d5fa0', // deeper purple for contrast
        primaryGlow: 'rgba(125, 95, 160, 0.15)',
        background: '#fcfbfd', // slightly off-white
        foreground: '#1f1529', // very dark purple (high contrast)
        card: '#ffffff',
        cardForeground: '#1f1529', // very dark purple
        muted: '#f0ebf5', // light lavender
        mutedForeground: '#3d2d51', // dark purple for readability
        accent: '#9370b8', // strong purple
        accentForeground: '#1f1529', // dark text on purple
      },
      dark: {
        primary: '#b695d1', // bright purple for visibility
        primaryGlow: 'rgba(182, 149, 209, 0.2)',
        background: '#0d0a12', // very dark for contrast
        foreground: '#f0ebf5', // light lavender text
        card: '#1f1529', // dark purple card
        cardForeground: '#f0ebf5', // light text on dark
        muted: '#2a1f3a', // muted purple
        mutedForeground: '#d4c4e0', // light for readability
        accent: '#c8aae0', // bright accent
        accentForeground: '#0d0a12', // very dark text
      }
    }
  },
  prism3d: {
    name: '3D-Prisma',
    nameEn: '3D-Prism',
    description: 'Futuristisch und innovativ',
    descriptionEn: 'Futuristic and innovative',
    icon: '💎',
    colors: {
      light: {
        primary: '#8b5cf6', // vibrant purple
        primaryGlow: 'rgba(139, 92, 246, 0.2)',
        background: '#faf9fb', // very light with blue tint
        foreground: '#1a0f2e', // deep blue-purple (high contrast)
        card: '#ffffff',
        cardForeground: '#1a0f2e', // deep blue-purple
        muted: '#ede9f5', // light purple-blue
        mutedForeground: '#3d2d5c', // dark purple for readability
        accent: '#ec4899', // vibrant pink
        accentForeground: '#ffffff', // white text on pink
      },
      dark: {
        primary: '#a78bfa', // bright purple for visibility
        primaryGlow: 'rgba(167, 139, 250, 0.3)',
        background: '#0f0a1a', // very dark blue-purple
        foreground: '#f0ebf8', // light purple-white text
        card: '#1a1229', // dark purple card
        cardForeground: '#f0ebf8', // light text on dark
        muted: '#251b3d', // muted purple
        mutedForeground: '#c4b5e0', // light purple for readability
        accent: '#f472b6', // bright pink
        accentForeground: '#0f0a1a', // very dark text
      }
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState('light'); // light or dark
  const [themeVariant, setThemeVariant] = useState('relaxation'); // relaxation, focus, mindset

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedMode = localStorage.getItem('theme-mode');
    const savedVariant = localStorage.getItem('theme-variant');
    
    if (savedMode) setMode(savedMode);
    if (savedVariant) setThemeVariant(savedVariant);
  }, []);

  // Convert hex to HSL
  const hexToHSL = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0 0% 0%';
    
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    h = Math.round(h * 360);
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    
    return `${h} ${s}% ${l}%`;
  };

  // Apply theme colors to CSS variables
  useEffect(() => {
    const theme = themes[themeVariant];
    const colors = theme.colors[mode];

    const root = document.documentElement;
    
    // Add theme class to body for CSS-based themes
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${themeVariant}`);
    
    // Add light/dark mode class
    if (mode === 'light') {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    } else {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    }
    
    // Apply Tailwind HSL variables (these are used by bg-primary, text-foreground, etc.)
    root.style.setProperty('--primary', hexToHSL(colors.primary));
    // Use accentForeground for primary-foreground to ensure proper contrast
    root.style.setProperty('--primary-foreground', hexToHSL(colors.accentForeground));
    root.style.setProperty('--background', hexToHSL(colors.background));
    root.style.setProperty('--foreground', hexToHSL(colors.foreground));
    root.style.setProperty('--card', hexToHSL(colors.card));
    root.style.setProperty('--card-foreground', hexToHSL(colors.cardForeground));
    root.style.setProperty('--muted', hexToHSL(colors.muted));
    root.style.setProperty('--muted-foreground', hexToHSL(colors.mutedForeground));
    root.style.setProperty('--accent', hexToHSL(colors.accent));
    root.style.setProperty('--accent-foreground', hexToHSL(colors.accentForeground));
    root.style.setProperty('--primary-glow', hexToHSL(colors.primary).replace(/[\d.]+%(?=\))/, (match) => `${parseFloat(match) + 20}%`));

    // Toggle dark class on html element for Tailwind
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [mode, themeVariant]);

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('theme-mode', newMode);
  };

  const changeThemeVariant = (variant) => {
    setThemeVariant(variant);
    localStorage.setItem('theme-variant', variant);
  };

  const value = {
    mode,
    themeVariant,
    currentTheme: themes[themeVariant],
    toggleMode,
    changeThemeVariant,
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
